import React, { useMemo } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuSubContent,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu"; // Adjust imports according to your setup
import {
  SCREEN_SIZES,
  SCREEN_SIZE_NAME_TO_SIZE,
  ScreenType,
} from "@/utils/constants";
import useUpdateStoryboardSize from "./hooks/useUpdateStoryboardSize"; // Assuming this is the path to your custom hook
import { Laptop, LucideIcon, Monitor, Smartphone, Tablet } from "lucide-react";

interface StoryboardResizeMenuProps {
  storyboard: any;
  canvas: any;
  "data-tooltip-id": string;
  "data-tooltip-content": string;
  className: string;
}

const StoryboardResizeMenu: React.FC<StoryboardResizeMenuProps> = React.memo(
  ({
    storyboard,
    canvas,
    "data-tooltip-id": tooltipId,
    "data-tooltip-content": tooltipContent,
    className,
  }) => {
    const { setSize } = useUpdateStoryboardSize(canvas);
    const handleSelectSize = (sizeName) => {
      const size = SCREEN_SIZE_NAME_TO_SIZE[sizeName];

      if (size) {
        if (size.tailwind) {
          setSize(storyboard, size.width, storyboard.height);
        } else {
          setSize(storyboard, size.width, size.height);
        }
      }
    };

    const Icons: Record<ScreenType, LucideIcon> = {
      [ScreenType.Mobile]: Smartphone,
      [ScreenType.Tablet]: Tablet,
      [ScreenType.Laptop]: Laptop,
      [ScreenType.Desktop]: Monitor,
    };

    const closestScreenType = useMemo(() => {
      let closestDistance = Infinity;
      let closestType = ScreenType.Desktop; // Default to Desktop

      SCREEN_SIZES.filter((screenSize) => !screenSize.tailwind).forEach(
        (screenSize) => {
          const distance = Math.sqrt(
            Math.pow(screenSize.width - storyboard.width, 2) +
              Math.pow(screenSize.height - storyboard.height, 2)
          );

          if (distance < closestDistance) {
            closestDistance = distance;
            closestType = screenSize.type;
          }
        }
      );

      return closestType;
    }, [storyboard.width, storyboard.height]);

    const TriggerIcon = Icons[closestScreenType];

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {
            <TriggerIcon
              data-tooltip-id={tooltipId}
              data-tooltip-content={tooltipContent}
              className={className}
            />
          }
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <img
                height={20}
                width={20}
                src="/images/tailwind-logo.svg"
                alt="Add a Tailwind Class"
                className="mr-2"
              />
              <span>Tailwind</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                {SCREEN_SIZES.filter((screenSize) => screenSize.tailwind).map(
                  (screenSize) => {
                    return (
                      <DropdownMenuItem
                        key={screenSize.name}
                        onSelect={() => handleSelectSize(screenSize.name)}
                      >
                        <span>{screenSize.name}</span>
                      </DropdownMenuItem>
                    );
                  }
                )}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
          <DropdownMenuGroup>
            {SCREEN_SIZES.filter((screenSize) => !screenSize.tailwind).map(
              (screenSize) => {
                const IconComponent = Icons[screenSize.type]; // Resolving the icon component here
                return (
                  <DropdownMenuItem
                    key={screenSize.name}
                    onSelect={() => handleSelectSize(screenSize.name)}
                  >
                    {Icons[screenSize.type] && (
                      <IconComponent className="w-5 h-5 mr-2" />
                    )}
                    <span>{screenSize.name}</span>
                  </DropdownMenuItem>
                );
              }
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

export default StoryboardResizeMenu;
